import { useState } from "react";
import type { VocabItem } from "../../types";
import { speakItalian as speak } from "../../lib/tts";

interface Props {
  vocab: VocabItem;
}

function SpeakerIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
    </svg>
  );
}

export default function FlashCard({ vocab }: Props) {
  const [flipped, setFlipped] = useState(false);

  const handlePlay = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    speak(text);
  };

  return (
    <div
      className="h-[170px] [perspective:900px] cursor-pointer"
      onClick={() => setFlipped((f) => !f)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setFlipped((f) => !f); }}
    >
      <div
        className={`relative w-full h-full [transform-style:preserve-3d] transition-transform duration-500 ${
          flipped ? "[transform:rotateY(180deg)]" : ""
        }`}
      >
        {/* Front — paper */}
        <div className="absolute inset-0 [backface-visibility:hidden] card flex flex-col items-center justify-center p-6 text-center">
          <button
            type="button"
            onClick={(e) => handlePlay(e, vocab.word)}
            aria-label={`Play pronunciation of ${vocab.word}`}
            className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center rounded-full text-text-tertiary hover:text-accent hover:bg-surface2 transition-colors"
          >
            <SpeakerIcon />
          </button>
          <div className="font-display text-[1.45rem] font-medium text-ink mb-2 leading-tight">{vocab.word}</div>
          <div className="text-[0.625rem] text-text-tertiary uppercase tracking-[0.16em] font-medium">tap to reveal</div>
        </div>

        {/* Back — dark surface */}
        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-[14px] bg-[#1f5062] flex flex-col items-center justify-center p-6 text-center shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)]">
          {vocab.example_sentence && (
            <button
              type="button"
              onClick={(e) => handlePlay(e, vocab.example_sentence!)}
              aria-label="Play example sentence"
              className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center rounded-full text-white/50 hover:text-white transition-colors"
            >
              <SpeakerIcon />
            </button>
          )}
          <div className="font-display text-xl text-white mb-2 leading-tight">{vocab.translation ?? "—"}</div>
          {vocab.example_sentence && (
            <div className="text-[0.8125rem] text-white/75 italic mb-3 leading-relaxed">"{vocab.example_sentence}"</div>
          )}
          {vocab.difficulty && (
            <span className="text-[0.6rem] uppercase tracking-[0.12em] py-1 px-2.5 rounded-full bg-white/15 text-white font-semibold">
              {vocab.difficulty}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
